import { supabase } from '../supabase';
import type {
  EventAudienceMemberRow,
  GroupWithMemberCount,
  UserRole,
} from '../types/database';

function getRpcErrorMessage(errorMessage: string): string {
  const cleanMessage = errorMessage.replace(/^ERROR:\s*/i, '').trim();
  return cleanMessage || 'Event targeting request failed.';
}

export async function listEventRoleTargets(
  eventId: number
): Promise<UserRole[]> {
  const { data, error } = await supabase.rpc('list_event_role_targets', {
    p_event_id: eventId,
  });

  if (error) {
    throw new Error(
      `Failed to load event role targets: ${getRpcErrorMessage(error.message)}`
    );
  }

  return ((data ?? []) as { role: UserRole }[]).map((target) => target.role);
}

export async function setEventRoleTargets(
  eventId: number,
  roles: UserRole[]
): Promise<UserRole[]> {
  const { data, error } = await supabase.rpc('set_event_role_targets', {
    p_event_id: eventId,
    p_roles: roles,
  });

  if (error) {
    throw new Error(getRpcErrorMessage(error.message));
  }

  return ((data ?? []) as { role: UserRole }[]).map((target) => target.role);
}

export async function listEventGroupTargets(
  eventId: number
): Promise<GroupWithMemberCount[]> {
  const { data, error } = await supabase.rpc('list_event_group_targets', {
    p_event_id: eventId,
  });

  if (error) {
    throw new Error(
      `Failed to load event group targets: ${getRpcErrorMessage(error.message)}`
    );
  }

  return (data ?? []) as GroupWithMemberCount[];
}

export async function setEventGroupTargets(
  eventId: number,
  groupIds: string[]
): Promise<GroupWithMemberCount[]> {
  const { data, error } = await supabase.rpc('set_event_group_targets', {
    p_event_id: eventId,
    p_group_ids: groupIds,
  });

  if (error) {
    throw new Error(getRpcErrorMessage(error.message));
  }

  return (data ?? []) as GroupWithMemberCount[];
}

export async function resolveEventAudience(
  eventId: number
): Promise<EventAudienceMemberRow[]> {
  const { data, error } = await supabase.rpc('resolve_event_audience', {
    p_event_id: eventId,
  });

  if (error) {
    throw new Error(
      `Failed to resolve event audience: ${getRpcErrorMessage(error.message)}`
    );
  }

  return (data ?? []) as EventAudienceMemberRow[];
}

export async function resolveEventAudiencePreview({
  roleTargets,
  groupTargetIds,
}: {
  roleTargets: UserRole[];
  groupTargetIds: string[];
}): Promise<EventAudienceMemberRow[]> {
  const { data, error } = await supabase.rpc('resolve_event_audience_preview', {
    p_roles: roleTargets,
    p_group_ids: groupTargetIds,
  });

  if (error) {
    throw new Error(
      `Failed to resolve event audience preview: ${getRpcErrorMessage(error.message)}`
    );
  }

  return (data ?? []) as EventAudienceMemberRow[];
}
